import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

export const isDateInRange = (startDate, endDate) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  return now >= start && now <= end;
};

const getEventsConfig = siteCopy => {
  if (_isEmpty(siteCopy)) {
    return {};
  }

  const eventsConfig = _get(siteCopy, 'pageCopy.timeSensitiveEvents', {});

  return eventsConfig;
};

const getDeprioritizedConfig = siteCopy => {
  if (_isEmpty(siteCopy)) {
    return [];
  }

  const deprioritizedConfig = _get(
    siteCopy,
    'pageCopy.deprioritizedKeywords',
    [],
  );

  return deprioritizedConfig;
};

const getActiveTimeSensitiveEvents = siteCopy => {
  const eventsConfig = getEventsConfig(siteCopy);
  const activeEvents = [];

  if (_isEmpty(eventsConfig)) {
    return activeEvents;
  }

  Object.entries(eventsConfig).forEach(([key, event]) => {
    if (!event.enabled) return;

    if (isDateInRange(event.startDate, event.endDate)) {
      activeEvents.push({
        key,
        ...event,
      });
    }
  });

  return activeEvents;
};

const queryContainsKeywords = (searchQuery, keywords) => {
  if (!searchQuery) return false;
  const lowerQuery = searchQuery.toLowerCase();
  return keywords.some(keyword => lowerQuery.includes(keyword.toLowerCase()));
};

const titleContainsKeywords = (title, keywords) => {
  if (!title || _isEmpty(keywords)) return false;
  const lowerTitle = title.toLowerCase();
  return keywords.some(keyword => lowerTitle.includes(keyword.toLowerCase()));
};

export const sortResourcesWithTimeSensitivity = (
  resources,
  searchQuery = '',
  siteCopy = {},
) => {
  if (_isEmpty(resources)) return resources;

  const activeEvents = getActiveTimeSensitiveEvents(siteCopy);
  const deprioritizedKeywords = getDeprioritizedConfig(siteCopy);

  const queryWords = searchQuery
    .toLowerCase()
    .split(' ')
    .filter(Boolean);
  const hasDetailedMatch = queryWords.length >= 2;

  const resourcesWithScores = resources.map(resource => {
    let scoreAdjustment = 0;

    const title = _get(resource, 'title', '');
    const name = _get(resource, 'name', '');
    const nameTitle = _get(resource, 'nameTitle', '');

    activeEvents.forEach(event => {
      if (
        titleContainsKeywords(title, event.keywords) &&
        queryContainsKeywords(title, queryWords)
      ) {
        scoreAdjustment += 150;
      } else if (
        titleContainsKeywords(name, event.keywords) &&
        queryContainsKeywords(name, queryWords)
      ) {
        scoreAdjustment += 100;
      } else if (
        titleContainsKeywords(nameTitle, event.keywords) &&
        queryContainsKeywords(nameTitle, queryWords)
      ) {
        scoreAdjustment += 100;
      }
    });

    if (!hasDetailedMatch && !_isEmpty(deprioritizedKeywords)) {
      if (!queryContainsKeywords(searchQuery, deprioritizedKeywords)) {
        if (
          titleContainsKeywords(title, deprioritizedKeywords) ||
          titleContainsKeywords(name, deprioritizedKeywords) ||
          titleContainsKeywords(nameTitle, deprioritizedKeywords)
        ) {
          scoreAdjustment -= 50;
        }
      }
    }

    return {
      ...resource,
      _clientSideScore: scoreAdjustment,
    };
  });

  const sorted = resourcesWithScores.sort((a, b) => {
    // eslint-disable-next-line no-underscore-dangle
    const scoreA = a._clientSideScore || 0;
    // eslint-disable-next-line no-underscore-dangle
    const scoreB = b._clientSideScore || 0;

    if (scoreA !== scoreB) {
      return scoreB - scoreA;
    }

    return 0;
  });

  // eslint-disable-next-line no-underscore-dangle
  return sorted.map(({ _clientSideScore, ...resource }) => resource);
};

export const getActiveResourceSlugs = (searchQuery = '', siteCopy = {}) => {
  const activeEvents = getActiveTimeSensitiveEvents(siteCopy);

  // If no search query, don't return any slugs
  if (!searchQuery || !searchQuery.trim()) {
    return [];
  }

  const slugs = [];
  activeEvents.forEach(event => {
    // Only include slugs if the search query contains at least one keyword from the event
    if (
      event.resourceSlugs &&
      Array.isArray(event.resourceSlugs) &&
      event.keywords &&
      queryContainsKeywords(searchQuery, event.keywords)
    ) {
      slugs.push(...event.resourceSlugs);
    }
  });

  return [...new Set(slugs)];
};
